import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCog, FaPlus, FaBriefcase, FaBuilding, FaCalendarAlt, FaUsers, FaInbox } from 'react-icons/fa';
import API from '../utils/api';
import EmployerApplicants from './EmployerApplicants';
import { useAuth } from '../context/AuthContext';

const EmployerJobs = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/jobs', {
        params: { postedBy: user?._id }
      });

      // Filter to show ONLY jobs posted by the current user
      const myJobs = (data.jobs || []).filter(job =>
        job.postedBy?._id === user?._id || job.postedBy === user?._id
      );

      setJobs(myJobs);
    } catch (err) {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-br from-[#00c2ab] to-[#009688] pt-16 pb-28 overflow-hidden">
        {/* Abstract Background Elements */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 rounded-full bg-white opacity-10 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-72 h-72 rounded-full bg-white opacity-10 blur-2xl"></div>
        
        <div className="container mx-auto px-4 relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-2 tracking-tight">My Posted Jobs</h1>
            <p className="text-lg text-teal-50 font-medium opacity-90">Manage your job listings and review applicants</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/manage-company"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white backdrop-blur-md px-6 py-3 rounded-xl font-bold transition-all shadow-sm hover:shadow-md border border-white/20"
            >
              <FaCog className="text-lg" /> Manage Company
            </Link>
            <Link
              to="/post-job"
              className="flex items-center gap-2 bg-white text-[#00c2ab] hover:bg-gray-50 hover:shadow-xl px-8 py-3 rounded-xl font-bold transition-all shadow-lg transform hover:-translate-y-0.5"
            >
              <FaPlus className="text-lg" /> Post a Job
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-12 relative z-20 max-w-6xl">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-xl border border-gray-100 p-10">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#00c2ab] mb-4"></div>
            <p className="text-lg font-bold text-gray-700">Loading your jobs...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-24 bg-white rounded-2xl shadow-xl border border-gray-100 p-10">
            <div className="w-24 h-24 bg-teal-50 rounded-full flex items-center justify-center mb-6 text-[#00c2ab]">
              <FaInbox className="text-5xl opacity-80" />
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">No jobs posted yet</h2>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">You haven't posted any jobs. Create your first job posting to start attracting top talent.</p>
            <Link
              to="/post-job"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white rounded-xl font-bold text-lg hover:shadow-[0_8px_25px_rgba(0,194,171,0.4)] transform hover:-translate-y-1 transition-all duration-300"
            >
              <FaPlus /> Create Your First Job
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map(job => (
              <div key={job._id} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl border border-gray-100 p-6 transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full animate-fadeIn">
                <div className="flex-1">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center text-[#00c2ab] mb-4">
                    <FaBriefcase className="text-2xl" />
                  </div>
                  <h3 className="text-xl font-extrabold text-gray-900 mb-4 line-clamp-2 leading-tight">
                    {job.title}
                  </h3>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-gray-600 font-medium">
                      <FaBuilding className="text-[#00c2ab] opacity-70" />
                      <span className="truncate">{job.company?.name || 'Unknown Company'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-500 text-sm">
                      <FaCalendarAlt className="text-[#00c2ab] opacity-70" />
                      <span>Posted: {job.createdAt ? new Date(job.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</span>
                    </div>
                  </div>
                </div>
                
                <div className="pt-6 border-t border-gray-100 mt-auto">
                  <button 
                    onClick={() => setSelectedJob(job._id)}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-teal-50 hover:bg-[#00c2ab] text-[#00c2ab] hover:text-white rounded-xl font-bold transition-all duration-300"
                  >
                    <FaUsers className="text-lg" />
                    Review Applicants
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedJob && (
        <EmployerApplicants jobId={selectedJob} onClose={() => setSelectedJob(null)} />
      )}
    </div>
  );
};

export default EmployerJobs;
